import {
  AfterContentInit,
  AfterViewInit,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NgProgressService } from '@shared/services/ng-progress.service';
import { CartService } from './cart.service';
import { CartItem } from './cart.model';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { FluidModule } from 'primeng/fluid';
import { Router } from '@angular/router';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { CardModule } from 'primeng/card';
import { SkeletonModule } from 'primeng/skeleton';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberInputEvent, InputNumberModule } from 'primeng/inputnumber';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ProfileService } from '../profile/profile.service';
import { CustomCurrencyPipe } from '@shared/pipes/custom-currency.pipe';
import { PopoverModule } from 'primeng/popover';

@Component({
  selector: 'app-cart',
  imports: [
    CommonModule,
    ButtonModule,
    FluidModule,
    AngularSvgIconModule,
    CardModule,
    SkeletonModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    ReactiveFormsModule,
    CustomCurrencyPipe,
    PopoverModule,
  ],
  templateUrl: './cart.component.html',
})
export class CartComponent implements OnInit, AfterViewInit, OnDestroy {
  form!: FormGroup;
  cart: CartItem[] = [];
  isLoading: boolean = true;
  totalPaymentAmount: number = 0;
  currency: string | undefined;
  isTouchDevice: boolean = false;

  private cartSub: Subscription = Subscription.EMPTY;

  private readonly _ngProgressService = inject(NgProgressService);
  private readonly _cartService = inject(CartService);
  private readonly _router = inject(Router);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _profileService = inject(ProfileService);

  get amounts(): FormArray {
    return this.form.get('amounts') as FormArray;
  }

  ngOnInit(): void {
    this.isTouchDevice = matchMedia('(pointer: coarse)').matches;
    this._ngProgressService.startLoader();

    this.currency = this._profileService.profile.currency;

    this.cartSub = this._cartService.cart$.subscribe((cart) => {
      this.cart = cart;

      // [Todo] Remove setTimeout latter
      setTimeout(() => {
        this.isLoading = false;
      }, 200);

      this.form = this._formBuilder.group({
        amounts: this._formBuilder.array([]),
      });

      this.cart.forEach((item) => this.addItemControl(item.amount));

      this.onUpdateTotalPaymentAmount();
    });

    this._cartService.getCart();
  }

  ngAfterViewInit(): void {
    this._ngProgressService.completeLoader();
  }

  onViewProperties(): void {
    this._router.navigate(['/home/properties']);
  }

  onRemoveProperty(index: number) {
    this._cartService.removeFromCart(index);

    this.amounts.removeAt(index);
  }

  onUpdateAmount(index: number, event: InputNumberInputEvent): void {
    this.cart[index].amount = +(event.value || 0);

    this.cart[index].monthlyCashflow =
      this._cartService.calculateMonthlyCashflow(
        this.cart[index].property,
        this.cart[index].amount
      );

    this.onUpdateTotalPaymentAmount();

    this._cartService.updateCartItem(
      this.cart[index],
      this.cart[index].property,
      0
    );
  }

  onUpdateTotalPaymentAmount(): void {
    this.totalPaymentAmount = this.amounts.controls
      .map((control) => control.value || 0)
      .reduce((sum, value) => sum + value, 0);
  }

  onProceedToPayment(): void {
    this._router.navigate(['home/cart/payment']);
  }

  private addItemControl(amount: number): void {
    const control = this._formBuilder.control(
      amount,
      [Validators.required, Validators.min(0.01)] // required and > 0
    );
    this.amounts.push(control);
  }

  ngOnDestroy(): void {
    this.cartSub.unsubscribe();
  }
}
